/**
 * MaintenanceBanner — aviso de ventana de mantenimiento o actualización, sobre
 * el header y visible en toda la app.
 *
 * Va arriba del header (junto a `EnvBanner`) y no dentro del contenido, porque
 * el punto es que se vea sin importar en qué pantalla esté el usuario.
 *
 * Comportamiento:
 *   - Se puede cerrar, y vuelve a aparecer al día siguiente (`maintenanceStore`
 *     guarda la fecha del cierre, no un booleano).
 *   - Cuando falta ≤ 1 h o el mantenimiento ya está en curso, NO se puede
 *     cerrar: a esa altura ya no es una preferencia del usuario.
 *   - Al pasar el fin de la ventana desaparece solo, porque el backend deja de
 *     servirlo.
 *
 * El estado (`scheduled` / `imminent` / `in_progress`) lo decide el backend: si
 * lo calculara el navegador, un reloj corrido mostraría "en curso" cuando no lo
 * está, o peor, lo contrario.
 */
import { AlertTriangle, Wrench, X } from 'lucide-react'
import { useActiveMaintenanceWindow } from '@/modules/platform/hooks/usePlatform'
import type { MaintenanceSeverity, MaintenanceState } from '@/modules/platform/hooks/usePlatform'
import { useMaintenanceStore } from '@/store/maintenanceStore'

const COLORES: Record<MaintenanceSeverity, string> = {
  info: 'bg-primary-600 text-white',
  warning: 'bg-warning-solid text-amber-950',
  critical: 'bg-danger-fill text-white',
}

function formatearRango(inicio: string, fin: string): string {
  const d1 = new Date(inicio)
  const d2 = new Date(fin)
  const fecha = d1.toLocaleDateString('es-CL', {
    weekday: 'long', day: 'numeric', month: 'long',
  })
  const hhmm = (d: Date) => d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  // Si termina el mismo día, no repetir la fecha.
  const mismoDia = d1.toDateString() === d2.toDateString()
  return mismoDia
    ? `${fecha} de ${hhmm(d1)} a ${hhmm(d2)}`
    : `desde el ${fecha} ${hhmm(d1)} hasta el ${d2.toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })} ${hhmm(d2)}`
}

function prefijo(state: MaintenanceState): string {
  if (state === 'in_progress') return 'Mantenimiento en curso'
  if (state === 'imminent') return 'Mantenimiento a punto de comenzar'
  return 'Mantenimiento programado'
}

export function MaintenanceBanner() {
  const { data } = useActiveMaintenanceWindow()
  const { isDismissedToday, dismiss } = useMaintenanceStore()

  const ventana = data?.window ?? null
  if (!ventana) return null
  // Solo los cerrables se pueden esconder: un `in_progress` o `imminent` se
  // muestra aunque el usuario lo haya cerrado antes, cuando todavía faltaba.
  if (ventana.can_dismiss && isDismissedToday(ventana.id)) return null

  const enCurso = ventana.state === 'in_progress'
  const Icono = enCurso ? Wrench : AlertTriangle

  return (
    <div
      role={enCurso || ventana.severity === 'critical' ? 'alert' : 'status'}
      aria-live={enCurso ? 'assertive' : 'polite'}
      className={`shrink-0 relative flex items-center justify-center gap-2.5 px-10 py-2 text-sm ${COLORES[ventana.severity]}`}
    >
      <Icono size={16} className={`flex-shrink-0 ${enCurso ? 'animate-pulse' : ''}`} aria-hidden="true" />
      <p className="text-center leading-snug">
        <span className="font-semibold">{ventana.title}</span>
        <span className="hidden sm:inline"> — {prefijo(ventana.state)} </span>
        <span className="hidden sm:inline opacity-90">{formatearRango(ventana.starts_at, ventana.ends_at)}.</span>
        {' '}
        <span className="opacity-95">{ventana.message}</span>
      </p>
      {ventana.can_dismiss && (
        <button
          onClick={() => dismiss(ventana.id)}
          aria-label="Cerrar aviso por hoy"
          title="Cerrar por hoy — volverá a aparecer mañana"
          className="absolute right-3 p-1.5 rounded-full hover:bg-black/20 transition-colors"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
